import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Home, Calendar, MapPin, Gift, CreditCard, CheckCircle, Clock, AlertCircle, Loader2, PartyPopper, Phone, FileText, Star } from "lucide-react";
import LivePartyTracker from "../components/LivePartyTracker";
import PaymentTrustBadge from "../components/PaymentTrustBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RatingCard = ({ booking, onRated }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (booking.rating) {
    return (
      <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8 p-5" data-testid="rating-submitted-card">
        <p className="text-sm font-semibold text-[#1E1B4B] mb-2">Thanks for rating your DoozyGo® experience</p>
        <div className="flex items-center gap-1" data-testid="rating-submitted-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <Star key={n} size={18} className={n <= booking.rating ? "text-amber-400 fill-amber-400" : "text-gray-200"} />
          ))}
          <span className="ml-2 text-sm text-[#475569]">{booking.rating}/5</span>
        </div>
      </div>
    );
  }

  const isRateable = ["completed", "setup_done", "delivered"].includes((booking.status || "").toLowerCase())
    || (booking.date && new Date(booking.date) <= new Date());

  if (!isRateable) return null;

  const submit = async () => {
    setError("");
    if (rating < 1) { setError("Please pick a star rating first"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/track/${booking.id}/rate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, review }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.detail || "Could not submit rating");
      } else {
        onRated(rating, review);
      }
    } catch { setError("Network error. Please try again."); }
    setSubmitting(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8 p-5" data-testid="rating-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center flex-shrink-0">
          <Star size={16} className="text-white fill-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-[#1E1B4B]">How was your DoozyGo® setup?</p>
          <p className="text-xs text-[#94a3b8]">Your rating helps us pick the best decorators and reward top performers.</p>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3" data-testid="rating-stars-row">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1 hover:scale-110 transition-transform"
            data-testid={`rating-star-${n}`}
            aria-label={`Rate ${n} out of 5`}
          >
            <Star
              size={28}
              className={n <= (hover || rating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}
            />
          </button>
        ))}
      </div>
      <textarea
        value={review}
        onChange={e => setReview(e.target.value.slice(0, 500))}
        placeholder="Tell us what the decorator did well (optional)"
        rows={2}
        className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm resize-none mb-3"
        data-testid="rating-review-input"
      />
      {error && <p className="text-red-500 text-xs mb-2" data-testid="rating-error">{error}</p>}
      <button
        onClick={submit}
        disabled={submitting || rating < 1}
        className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="rating-submit-btn"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
        {submitting ? "Submitting..." : "Submit Rating"}
      </button>
    </div>
  );
};

const StatusBadge = ({ status, paymentStatus, balanceDue }) => {
  const isFullyPaid = balanceDue <= 0 && paymentStatus !== "pending";
  const isCancelled = status === "cancelled";
  const hasDue = balanceDue > 0;

  if (isCancelled) return (
    <span data-testid="status-badge-cancelled" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <AlertCircle size={14} /> Cancelled
    </span>
  );
  if (isFullyPaid) return (
    <span data-testid="status-badge-paid" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
      <CheckCircle size={14} /> Fully Paid
    </span>
  );
  if (hasDue) return (
    <span data-testid="status-badge-balance" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
      <Clock size={14} /> Balance Due
    </span>
  );
  return (
    <span data-testid="status-badge-booked" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
      <CheckCircle size={14} /> Booked
    </span>
  );
};

const TrackBooking = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const fetchBooking = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/track/${bookingId}`);
      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        console.debug("[TrackBooking] JSON parse failed:", parseErr?.message);
        throw new Error("Unable to load booking details");
      }
      if (!res.ok) {
        throw new Error(data?.detail || "Booking not found");
      }
      setBooking(data);
    } catch (e) {
      setError(e.message || "Unable to load booking");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const handlePayBalance = async () => {
    if (!booking || booking.balance_due <= 0) return;
    setPaying(true);
    try {
      const res = await fetch(`${API_URL}/api/track/${bookingId}/pay-balance`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Payment creation failed");
      }
      const order = await res.json();

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "DoozyGo®",
        description: `Balance Payment - ${bookingId}`,
        order_id: order.order_id,
        prefill: {
          name: order.customer_name,
          email: order.customer_email,
          contact: order.customer_phone,
        },
        theme: { color: "#E83E8C" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API_URL}/api/track/${bookingId}/verify-balance-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (verifyRes.ok) {
              setPaymentSuccess(true);
              fetchBooking();
            } else {
              const err = await verifyRes.json();
              setError(err.detail || "Payment verification failed");
            }
          } catch {
            setError("Payment verification failed. Please contact support.");
          }
          setPaying(false);
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        throw new Error("Payment gateway not loaded. Please refresh.");
      }
    } catch (e) {
      setError(e.message);
      setPaying(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00"));
      return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E83E8C]" size={40} />
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-[#1E1B4B] mb-2">Booking Not Found</h2>
          <p className="text-[#475569] mb-6">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#E83E8C] hover:underline font-medium">
            <Home size={16} /> Go to DoozyGo® Home
          </Link>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const isFullyPaid = booking.balance_due <= 0 && booking.paid_amount > 0;

  return (
    <div className="min-h-screen bg-[#FFFAF5]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] py-6 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png"
              alt="DoozyGo®"
              className="h-10"
            />
          </Link>
          <span className="text-white/80 text-sm font-mono">{booking.id}</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 -mt-2">
        {/* Payment Success Banner */}
        {paymentSuccess && (
          <div data-testid="payment-success-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="text-emerald-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-emerald-800">Payment Successful!</p>
              <p className="text-emerald-600 text-sm">Your balance payment has been received. Thank you!</p>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && booking && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Live Party Progress Tracker */}
        <LivePartyTracker bookingId={bookingId} />

        {/* Booking Card */}
        <div data-testid="booking-details-card" className="bg-white rounded-2xl shadow-md overflow-hidden mb-4">
          {/* Status Header */}
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-lg font-bold text-[#1E1B4B]">Your Booking</h1>
              <StatusBadge status={booking.status} paymentStatus={booking.payment_status} balanceDue={booking.balance_due} />
            </div>
            <p className="text-[#475569] text-sm">Hi {booking.customer_name}, here are your party details</p>
          </div>

          {/* Details */}
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-[#E83E8C] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-medium">Party Date</p>
                <p data-testid="booking-date" className="text-[#1E1B4B] font-semibold">{formatDate(booking.date)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <PartyPopper size={18} className="text-[#FF9F1C] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-medium">Package</p>
                <p data-testid="booking-package" className="text-[#1E1B4B] font-semibold">{booking.package}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Gift size={18} className="text-purple-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-medium">Occasion</p>
                <p data-testid="booking-occasion" className="text-[#1E1B4B] font-semibold">{booking.occasion}</p>
              </div>
            </div>

            {booking.address && (
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-teal-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-medium">Location</p>
                  <p className="text-[#1E1B4B]">{booking.address}</p>
                </div>
              </div>
            )}

            {booking.add_ons && booking.add_ons.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-[#94a3b8] uppercase tracking-wider font-medium mb-2">Add-ons</p>
                <div className="space-y-1">
                  {booking.add_ons.map((addon, i) => (
                    <div key={addon.name || `addon-${i}`} className="flex justify-between text-sm">
                      <span className="text-[#475569]">{addon.name} x{addon.quantity || 1}</span>
                      {addon.price > 0 && <span className="text-[#1E1B4B]">Rs {addon.price}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Card */}
        <div data-testid="payment-card" className="bg-white rounded-2xl shadow-md overflow-hidden mb-4">
          <div className="p-5">
            <h2 className="text-base font-bold text-[#1E1B4B] mb-4">Payment Summary</h2>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#475569] text-sm">Package Price</span>
                <span data-testid="package-price" className="text-[#1E1B4B] font-semibold">Rs {booking.package_price?.toLocaleString("en-IN") || "0"}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[#475569] text-sm">Amount Paid</span>
                <span data-testid="amount-paid" className="text-emerald-600 font-semibold">Rs {booking.paid_amount?.toLocaleString("en-IN") || "0"}</span>
              </div>

              <div className="border-t border-dashed border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#1E1B4B] font-bold">Balance Due</span>
                  <span data-testid="balance-due" className={`text-lg font-bold ${booking.balance_due > 0 ? "text-[#E83E8C]" : "text-emerald-600"}`}>
                    {booking.balance_due > 0 ? `Rs ${booking.balance_due.toLocaleString("en-IN")}` : "Nil"}
                  </span>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            {booking.balance_due > 0 && booking.status !== "cancelled" && (
              <div className="mt-5 space-y-2">
                <PaymentTrustBadge variant="compact" />
                <button
                  data-testid="pay-balance-btn"
                  onClick={handlePayBalance}
                  disabled={paying}
                  className="w-full py-3.5 rounded-xl font-bold text-white text-base
                    bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] hover:shadow-lg
                    transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {paying ? (
                    <><Loader2 className="animate-spin" size={18} /> Processing...</>
                  ) : (
                    <><CreditCard size={18} /> Pay Rs {booking.balance_due.toLocaleString("en-IN")} Now</>
                  )}
                </button>
              </div>
            )}

            {isFullyPaid && (
              <div data-testid="fully-paid-msg" className="mt-4 text-center py-3 bg-emerald-50 rounded-xl">
                <p className="text-emerald-700 font-semibold flex items-center justify-center gap-2">
                  <CheckCircle size={18} /> All payments complete
                </p>
              </div>
            )}

            {(booking.paid_amount > 0 || booking.payment_status === "paid" || booking.payment_status === "partial") && (
              <a
                href={`${API_URL}/api/bookings/invoice.pdf?booking_id=${encodeURIComponent(booking.id)}&phone=${encodeURIComponent(booking.customer_phone || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#1E1B4B]/20 text-[#1E1B4B] font-semibold text-sm hover:bg-[#1E1B4B]/5 transition"
                data-testid="download-invoice-btn"
              >
                <FileText size={16} /> Download tax invoice (PDF)
              </a>
            )}
          </div>
        </div>

        {/* Rating Card (post-setup only) */}
        <RatingCard
          booking={booking}
          onRated={(r, rev) => setBooking(prev => ({ ...prev, rating: r, rating_review: rev, rating_submitted_at: new Date().toISOString() }))}
        />

        {/* Contact Card */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
          <div className="p-5 text-center">
            <p className="text-[#475569] text-sm mb-3">Questions about your booking?</p>
            <a
              href="https://wa.me/919930001848"
              data-testid="contact-whatsapp-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1fb855] transition"
            >
              <Phone size={16} /> Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-[#94a3b8] text-xs">Big Kreation Entertainment Pvt Ltd</p>
          <p className="text-[#94a3b8] text-xs">GSTIN: 27AAMCB6393G1Z9</p>
        </div>
      </div>
    </div>
  );
};

export default TrackBooking;
